import {
  BreadcrumbGroup,
  Header,
  SpaceBetween,
  Box,
  Container,
  Select,
  FormField,
  TokenGroup,
  TokenGroupProps,
  NonCancelableCustomEvent,
  Button,
  Form,
  StatusIndicator,
  Spinner,
  Alert,
  Input,
  Link,
} from '@amzn/awsui-components-react-v3';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import SiteInput from '../../../components/SiteInput';
import optionTypes from '../mocks/optionTypes.json';
import '../style.css';
import {
  GET_CONTAINER_PERMISSIONS,
  MANAGE_CONTAINER_PERMISSIONS,
  GET_CMS_CONTAINER_BY_ASSET_TAG,
  VALIDATE_CONTAINER_MOVEMENT,
  MOVE_CONTAINERS,
} from '../../../services/container-management/queries';
import ContainerSeals from '../../../components/ContainerManagement/ContainerSeals';
import Logger from '../../../utils/Logger';
import { CmsContainer } from '../../../services/container-management/types';
import { validateInputWithDots } from '../../../utils';

/**
 * Component for Container Move Page
 * @component
 * @returns {ReactElement}
 */
const ContainerMove = (): ReactElement => {
  const { t } = useTranslation();

  const [DestinationTypeSelectedOption, setDestinationTypeSelectedOption] = React.useState(null);
  const [DestinationTypeSelectedValue, setDestinationTypeSelectedValue] =
    React.useState<string>('');

  const [destinationNameValue, setDestinationNameValue] = useState('');
  const [userAlias, setUserAlias] = useState('');

  const [sourceContainerNameValues, setSourceContainerNameValues] = useState<any[]>([]);
  const [sourceContainerNameInput, setSourceContainerNameInput] = useState('');

  const [errorMessageForDestination, setErrorMessageForDestination] = useState('');
  const [errorElementForDestination, setErrorElementForDestination] = useState<JSX.Element | null>(
    null
  );
  const [errorMessageForMovementValidation, setErrorMessageForMovementValidation] = useState('');
  const [errorMessageForMovements, setErrorMessageForMovements] = useState('');

  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [isDestinationVerified, setIsDestinationVerified] = React.useState(false);

  const [moveToCustodyFirst, setMoveToCustodyFirst] = React.useState(false);
  const [numOfNotInCustodyContainers, setNumOfNotInCustodyContainers] = React.useState(0);

  const [isMovementsSuccess, setIsMovementsSuccess] = React.useState(false);
  const [visibleSuccess, setVisibleSuccess] = useState(true);

  const [destinationContainer, setDestinationContainer] = React.useState<CmsContainer>({
    parentlocation: [{ type: '', value: '' }],
    containerDefinitionId: '',
    name: '',
    containerId: '',
    containerName: '',
    isActive: false,
    size: '',
    locationId: '',
    metadata: {
      alphabetIdentifier: '',
      section: '',
      aisle: '',
      level: '',
      isCompliant: false,
      isEmpty: false,
      isLabelPrinted: false,
    },
    version: 0,
    seals: [],
    policyIds: [],
  });

  const [location, setLocation] = React.useState(() => {
    const loc = localStorage.getItem('siteId');
    if (loc) {
      return JSON.parse(loc).label;
    }
    return '';
  });

  const [lastSiteIDValue, setLastSiteIDValue] = React.useState(() => {
    const loc = localStorage.getItem('siteId');
    if (loc) {
      return JSON.parse(loc).locationId;
    }
    return '';
  });

  const breadcrumbs = [
    {
      text: t('breadcrumb.home'),
      href: '/',
    },
    {
      text: t('breadcrumb.inventory-management'),
      href: '/assets-inventory/',
    },
    {
      text: t('breadcrumb.inventory-move'),
      href: '/assets-inventory/move',
    },
    {
      text: t('breadcrumb.container-move'),
      href: '/assets-inventory/move/container',
    },
  ];

  const resetFields = () => {
    setSourceContainerNameValues([]);

    setErrorMessageForDestination('');
    setErrorMessageForMovementValidation('');
    setErrorMessageForMovements('');

    setIsSubmitted(false);

    setMoveToCustodyFirst(false);
    setNumOfNotInCustodyContainers(0);

    setIsMovementsSuccess(false);
    setVisibleSuccess(true);

    setDestinationNameValue('');
    setSourceContainerNameInput('');
  };

  const resetType = () => {
    setDestinationTypeSelectedOption(null);
    setDestinationTypeSelectedValue('');
    resetFields();
  };

  const handleDestinationTypeChange = (obj: any) => {
    setDestinationTypeSelectedOption(obj);
    setDestinationTypeSelectedValue(obj.value || '');
    resetFields();
  };

  const handleTakeToCustody = async () => {
    setErrorMessageForMovementValidation('');
    const containersToCustody = [];
    for (let i = 0; i < sourceContainerNameValues.length; i += 1) {
      if (sourceContainerNameValues[i].description) {
        containersToCustody.push(sourceContainerNameValues[i].label);
        sourceContainerNameValues[i].description = null;
      }
    }
    await moveContainers({
      variables: {
        sourceContainerNames: containersToCustody,
        destinationType: 'CUSTODY',
        destinationName: userAlias,
      },
    });
  };

  const handleAddTag = async () => {
    if (!sourceContainerNameInput) {
      return;
    }
    if (sourceContainerNameInput === destinationNameValue) {
      setErrorMessageForMovementValidation(
        'the source container and the destination container must not be the same'
      );
      return;
    }
    if (
      sourceContainerNameValues.every(
        (i: TokenGroupProps.Item) => i.label !== sourceContainerNameInput
      )
    ) {
      if (sourceContainerNameValues.length > 5) {
        setErrorMessageForMovementValidation('Only 5 containers can be moved at once');
      } else {
        await validateContainerMovement({
          variables: {
            sourceContainerName: sourceContainerNameInput,
            destinationType: DestinationTypeSelectedValue,
            destinationName: destinationNameValue,
          },
        });
      }
    } else {
      setErrorMessageForMovementValidation('container already added');
    }
  };

  const handleRemoveTag = (event: NonCancelableCustomEvent<TokenGroupProps.DismissDetail>) => {
    if (sourceContainerNameValues[event.detail.itemIndex].description) {
      const num = numOfNotInCustodyContainers;
      setNumOfNotInCustodyContainers(num - 1);
    }
    if (sourceContainerNameInput === sourceContainerNameValues[event.detail.itemIndex].label) {
      setErrorMessageForMovementValidation('');
    }
    setSourceContainerNameValues([
      ...sourceContainerNameValues.slice(0, event.detail.itemIndex),
      ...sourceContainerNameValues.slice(event.detail.itemIndex + 1),
    ]);
  };

  const renderSuccessAlerts = () => {
    if (isMovementsSuccess) {
      return (
        <Alert
          onDismiss={() => setVisibleSuccess(false)}
          visible={visibleSuccess}
          dismissAriaLabel="Close alert"
          dismissible
          type="success"
        >
          {`${sourceContainerNameValues.length} containers have been successfully moved. `}
        </Alert>
      );
    }
    if (errorMessageForMovements !== '') {
      return (
        <Alert dismissAriaLabel="Close alert" dismissible type="error">
          {errorMessageForMovements}
        </Alert>
      );
    }
    return <div />;
  };

  const createDropDown = (option: any, onchange: any, options: any, testId: any) => {
    return (
      <Select
        data-test-id={testId}
        selectedOption={option}
        onChange={({ detail }) => onchange(detail.selectedOption)}
        options={options}
        placeholder="Choose"
        selectedAriaLabel="Selected"
        empty="No options"
      />
    );
  };

  const renderSubmitOrAnotherMove = () => {
    if (!isMovementsSuccess) {
      return (
        <Button
          data-test-id="submit"
          loading={moveContainersLoading}
          variant="primary"
          disabled={
            userAlias === '' ||
            moveToCustodyFirst ||
            sourceContainerNameValues.length === 0 ||
            moveContainersLoading ||
            errorMessageForMovements !== ''
          }
          onClick={callMoveContainers}
        >
          {t('Submit')}
        </Button>
      );
    }
    return (
      <Button data-test-id="another-move" variant="primary" onClick={resetType}>
        {t('Another Move')}
      </Button>
    );
  };

  const renderResetButton = () => {
    if (DestinationTypeSelectedValue) {
      return (
        <SpaceBetween direction="horizontal" size="xxl">
          <div>
            <Button
              data-test-id="reset"
              disabled={isSubmitted}
              iconAlign="right"
              onClick={resetType}
            >
              {t('Reset')}
            </Button>
          </div>
        </SpaceBetween>
      );
    }
    return <div />;
  };

  const renderFormActions = () => {
    if (DestinationTypeSelectedValue) {
      return (
        <SpaceBetween direction="horizontal" size="xs">
          <Button href="/assets-inventory/move" variant="link">
            {t('Return Home')}
          </Button>
          {moveToCustodyFirst && (
            <Button
              data-test-id="take-to-custody"
              loading={moveContainersLoading}
              disabled={moveContainersLoading || userAlias === ''}
              variant="primary"
              onClick={handleTakeToCustody}
            >
              {t('Take scanned containers into user custody')}
            </Button>
          )}
          {renderSubmitOrAnotherMove()}
        </SpaceBetween>
      );
    }
    return <div />;
  };

  const renderSourceContainerInputBox = () => {
    return (
      <div>
        <FormField
          label={t('Scan Container to be moved')}
          errorText={errorMessageForMovementValidation}
        >
          <div className="flex-align-center">
            <div className="input-box-style">
              <Input
                data-test-id="input-source-container"
                value={sourceContainerNameInput}
                onChange={({ detail }) => {
                  const validationError = validateInputWithDots(detail.value.trim(), true, 100);
                  if (validationError !== '') {
                    setErrorMessageForMovementValidation(validationError);
                  } else {
                    setErrorMessageForMovementValidation('');
                  }
                  setSourceContainerNameInput(detail.value.trim());
                }}
                onKeyDown={(e) => {
                  // Check for special characters and prevent input
                  if (errorMessageForMovementValidation === '' && e.detail.keyCode === 13) {
                    handleAddTag();
                  }
                }}
                placeholder={t('Scan or enter the barcode on the container')}
                disabled={
                  !isDestinationVerified ||
                  isMovementsSuccess ||
                  userAlias === '' ||
                  errorMessageForMovements !== ''
                }
              />
            </div>
            {validateContainerMovementLoading && (
              <div>
                <Spinner size="normal" />
                <span> Validating </span>
              </div>
            )}
            {errorMessageForMovementValidation &&
              errorMessageForMovementValidation.includes('Please use CMS to create container') && (
                <Link href="/assets-inventory/container/create">To create container</Link>
              )}
          </div>
        </FormField>
        <div className="token-group-style">
          <TokenGroup
            data-test-id="list-source-containers"
            onDismiss={handleRemoveTag}
            items={sourceContainerNameValues}
            alignment="vertical"
            limit={5}
          />
        </div>
      </div>
    );
  };

  const [callGetContainerPermissions, { loading: getContainerPermissionsLoading }] = useLazyQuery(
    GET_CONTAINER_PERMISSIONS,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      onCompleted: (result) => {
        setUserAlias(result?.getContainerPermissionsCheck?.user);
      },
    }
  );

  const [callManageContainerPermissions, { loading: manageContainerPermissionsLoading }] =
    useLazyQuery(MANAGE_CONTAINER_PERMISSIONS, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      onCompleted: (result) => {
        setUserAlias(result?.manageContainerPermissionsCheck?.user);
      },
    });

  const [getCmsContainerByAssetTag, { loading: getContainerByAssetTagLoading }] = useLazyQuery(
    GET_CMS_CONTAINER_BY_ASSET_TAG,
    {
      fetchPolicy: 'no-cache',
      onCompleted: (result) => {
        if (result) {
          if (!result?.getCmsContainerByAssetTag.container.isActive) {
            setErrorMessageForDestination('Container is not active');
          } else if (
            result?.getCmsContainerByAssetTag.container.seals &&
            result?.getCmsContainerByAssetTag.container.seals.length > 0
          ) {
            setErrorMessageForDestination('Parent container should not have active seals');
          } else if (
            result?.getCmsContainerByAssetTag.containerDefinition.containerType === 'bin' &&
            destinationNameValue.split('.')[0] !== location
          ) {
            setErrorMessageForDestination('Bin is not at this site');
          } else {
            setIsDestinationVerified(true);
            setErrorMessageForDestination('');
            setDestinationContainer(result?.getCmsContainerByAssetTag.container);
          }
        }
      },
      onError: (error) => {
        if (error.message.includes(`Container ${destinationNameValue} not found`)) {
          setErrorElementForDestination(
            <>
              {`Container: "${destinationNameValue}" not found. Please use CMS to `}
              <Link href="/assets-inventory/container/create">create container</Link>
            </>
          );
        } else {
          setErrorMessageForDestination(error.message);
        }
      },
    }
  );

  const callGetCmsContainerByAssetTag = async () => {
    if (destinationNameValue) {
      Logger.debug(`Calling getContainerByAssetTag with ${destinationNameValue}`);
      await getCmsContainerByAssetTag({ variables: { assetTag: destinationNameValue } });
    }
  };

  const [validateContainerMovement, { loading: validateContainerMovementLoading }] = useLazyQuery(
    VALIDATE_CONTAINER_MOVEMENT,
    {
      fetchPolicy: 'network-only',
      onCompleted: (result) => {
        if (result?.validateContainerMovement?.movementValidated) {
          setSourceContainerNameValues([
            { label: sourceContainerNameInput, disabled: false },
            ...sourceContainerNameValues,
          ]);
        }
        setSourceContainerNameInput('');
      },
      onError: (error) => {
        if (
          error.message.includes('Container is not in ') &&
          error.message.includes("'s custody")
        ) {
          const num = numOfNotInCustodyContainers;
          setNumOfNotInCustodyContainers(num + 1);
          setMoveToCustodyFirst(true);
          setSourceContainerNameValues([
            {
              label: sourceContainerNameInput,
              description: error.message,
              disabled: false,
            },
            ...sourceContainerNameValues,
          ]);
        } else if (error.message.includes(`Container ${sourceContainerNameInput} not found`)) {
          setErrorMessageForMovementValidation(
            `container: "${sourceContainerNameInput}" not found. Please use CMS to create container`
          );
        } else if (error.message.includes(`Container definition is not movable`)) {
          setErrorMessageForMovementValidation(`container is not movable`);
        } else {
          setErrorMessageForMovementValidation(error.message);
        }
      },
    }
  );

  const [moveContainers, { loading: moveContainersLoading }] = useMutation(MOVE_CONTAINERS, {
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      if (!moveToCustodyFirst) {
        setIsMovementsSuccess(true);
        setIsDestinationVerified(false);
      } else {
        setNumOfNotInCustodyContainers(0);
        setMoveToCustodyFirst(false);
      }
    },
    onError: (error) => {
      setIsMovementsSuccess(false);
      setErrorMessageForMovements('There was an issue moving containers. Please try again.');
    },
  });

  const callMoveContainers = async () => {
    Logger.debug(`Calling moveContainers`);
    setErrorMessageForMovementValidation('');
    setSourceContainerNameInput('');
    const containersToMove = [];
    for (let i = 0; i < sourceContainerNameValues.length; i += 1) {
      containersToMove.push(sourceContainerNameValues[i].label);
      sourceContainerNameValues[i].disabled = true;
    }
    await moveContainers({
      variables: {
        sourceContainerNames: containersToMove,
        destinationType: DestinationTypeSelectedValue,
        destinationName: destinationNameValue,
        lastSiteID: lastSiteIDValue,
      },
    });
  };

  useEffect(() => {
    callGetContainerPermissions();
    callManageContainerPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      DestinationTypeSelectedValue === 'CUSTODY' &&
      !getContainerPermissionsLoading &&
      !manageContainerPermissionsLoading &&
      userAlias !== ''
    ) {
      setDestinationNameValue(userAlias);
    }
  }, [
    DestinationTypeSelectedValue,
    getContainerByAssetTagLoading,
    getContainerPermissionsLoading,
    manageContainerPermissionsLoading,
    userAlias,
  ]);

  useEffect(() => {
    if (DestinationTypeSelectedValue === 'CONTAINER') {
      setIsDestinationVerified(false);
      setErrorMessageForDestination('');
      setSourceContainerNameInput('');
      setErrorMessageForMovementValidation('');
      setSourceContainerNameValues([]);
      callGetCmsContainerByAssetTag();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (isDestinationVerified) {
      setErrorMessageForDestination('');
    }
    if (getContainerByAssetTagLoading) {
      setErrorMessageForDestination('');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDestinationVerified, getContainerByAssetTagLoading]);

  useEffect(() => {
    if (validateContainerMovementLoading) {
      setErrorMessageForMovementValidation('');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateContainerMovementLoading]);

  useEffect(() => {
    if (DestinationTypeSelectedValue === 'CUSTODY') {
      setIsDestinationVerified(true);
    } else {
      setIsDestinationVerified(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DestinationTypeSelectedValue]);

  useEffect(() => {
    if (numOfNotInCustodyContainers === 0) {
      setMoveToCustodyFirst(false);
    } else {
      setMoveToCustodyFirst(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numOfNotInCustodyContainers]);

  return (
    <div className="formWidth">
      <SpaceBetween direction="vertical" size="s">
        <BreadcrumbGroup items={breadcrumbs} />
        {renderSuccessAlerts()}
        <SpaceBetween direction="vertical" size="xs">
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <SiteInput
                onSiteChange={(loc) => {
                  setLocation(loc?.label);
                  setLastSiteIDValue(loc?.locationId);
                }}
              />
            </SpaceBetween>
          </Box>

          <Container header={<Header variant="h2">Container Move</Header>}>
            <Form actions={renderFormActions()}>
              <div className="reset-button-style">
                <Box float="right">{renderResetButton()}</Box>
              </div>
              <SpaceBetween direction="vertical" size="l">
                <FormField label="Destination Type">
                  <div className="dropDownStyle select-menu-style">
                    {createDropDown(
                      DestinationTypeSelectedOption,
                      handleDestinationTypeChange,
                      optionTypes['destination-types'],
                      'destination-type-selection'
                    )}
                  </div>
                </FormField>

                {DestinationTypeSelectedValue === 'CONTAINER' && (
                  <SpaceBetween direction="vertical" size="l">
                    <FormField
                      label={<span>Scan destination container</span>}
                      errorText={errorMessageForDestination || errorElementForDestination}
                    >
                      <div className="flex-align-center">
                        <div className="input-box-style">
                          <Input
                            data-test-id="input-destination-container"
                            type="text"
                            value={destinationNameValue}
                            disabled={
                              isMovementsSuccess ||
                              userAlias === '' ||
                              errorMessageForMovements !== ''
                            }
                            onChange={({ detail }) => {
                              const validationError = validateInputWithDots(
                                detail.value.trim(),
                                true,
                                100
                              );
                              if (validationError !== '') {
                                setErrorMessageForDestination(validationError);
                              } else {
                                setErrorMessageForDestination('');
                              }
                              setDestinationNameValue(detail.value.trim());
                              setIsDestinationVerified(false);
                              setSourceContainerNameInput('');
                              setErrorMessageForMovementValidation('');
                              setSourceContainerNameValues([]);
                            }}
                            onKeyDown={(e) => {
                              if (errorMessageForDestination === '' && e.detail.keyCode === 13) {
                                callGetCmsContainerByAssetTag();
                              }
                            }}
                            placeholder={t('Scan or enter the barcode on the container')}
                          />
                        </div>
                        {isDestinationVerified && !getContainerByAssetTagLoading && (
                          <div>
                            <StatusIndicator type="success">verified</StatusIndicator>
                          </div>
                        )}
                        {getContainerByAssetTagLoading && (
                          <div>
                            <Spinner size="normal" />
                            <span> Validating </span>
                          </div>
                        )}
                        {errorMessageForDestination &&
                          errorMessageForDestination.includes(
                            'Please use CMS to create container'
                          ) && (
                            <Link href="/assets-inventory/container/create">
                              To create container
                            </Link>
                          )}
                      </div>
                    </FormField>
                    <div className="container-height">
                      <SpaceBetween direction="vertical" size="l">
                        {renderSourceContainerInputBox()}
                        {isMovementsSuccess && (
                          <ContainerSeals
                            container={destinationContainer}
                            containerFullName={destinationNameValue}
                          />
                        )}
                      </SpaceBetween>
                    </div>
                  </SpaceBetween>
                )}

                {DestinationTypeSelectedValue === 'CUSTODY' && (
                  <SpaceBetween direction="vertical" size="l">
                    <FormField label="User alias">
                      <p>{userAlias}</p>
                    </FormField>
                    <div className="container-height">{renderSourceContainerInputBox()}</div>
                  </SpaceBetween>
                )}
              </SpaceBetween>
            </Form>
          </Container>
        </SpaceBetween>
      </SpaceBetween>
    </div>
  );
};

export default ContainerMove;
